import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Package } from "../lib/api";
import { formatPrice, fromEUR } from "../lib/format";

// ─── PackagesPage ─────────────────────────────────────────────────────────────

export function PackagesPage() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPriceEUR, setNewPriceEUR] = useState("");
  const [newIsPublic, setNewIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editID, setEditID] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriceEUR, setEditPriceEUR] = useState("");
  const [editIsPublic, setEditIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listPackages();
      setPackages(list ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const priceCents = newPriceEUR ? Math.round(parseFloat(newPriceEUR.replace(",", ".")) * 100) : 0;
      await api.createPackage({
        name: newName.trim(),
        description: newDescription.trim(),
        price_cents: priceCents,
        currency: "EUR",
        is_public: newIsPublic,
      });
      setNewName("");
      setNewDescription("");
      setNewPriceEUR("");
      setNewIsPublic(true);
      setShowNew(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  }

  function startEdit(p: Package) {
    setEditID(p.ID);
    setEditName(p.Name);
    setEditDescription(p.Description);
    setEditPriceEUR(fromEUR(p.PriceCents));
    setEditIsPublic(p.IsPublic);
  }

  function cancelEdit() {
    setEditID(null);
  }

  async function handleSave(p: Package) {
    setSaving(true);
    setError(null);
    try {
      const priceCents = editPriceEUR ? Math.round(parseFloat(editPriceEUR.replace(",", ".")) * 100) : 0;
      await api.updatePackage(p.ID, {
        name: editName.trim(),
        description: editDescription.trim(),
        price_cents: priceCents,
        is_public: editIsPublic,
      });
      setEditID(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this package? This cannot be undone.")) return;
    setDeletingID(id);
    try {
      await api.deletePackage(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingID(null);
    }
  }

  async function moveUp(index: number) {
    if (index === 0 || moving) return;
    const ids = packages.map(p => p.ID);
    [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
    setMoving(true);
    try {
      await api.reorderPackages(ids);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed");
    } finally {
      setMoving(false);
    }
  }

  async function moveDown(index: number) {
    if (index === packages.length - 1 || moving) return;
    const ids = packages.map(p => p.ID);
    [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
    setMoving(true);
    try {
      await api.reorderPackages(ids);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reorder failed");
    } finally {
      setMoving(false);
    }
  }

  return (
    <div>
      <h1 style={{ margin: "0 0 1rem" }}>{t("packages.title")}</h1>

      {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {showNew && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginTop: 0 }}>{t("packages.new_form_title")}</h3>
          <form onSubmit={handleCreate} style={{ display: "grid", gap: "0.75rem" }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>{t("packages.name_label")}</label>
              <input required value={newName} onChange={e => setNewName(e.target.value)} placeholder="Wedding full day" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>{t("packages.description_label")}</label>
              <textarea rows={2} value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="Full day coverage, 2 photographers, 400+ edited photos…" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>{t("packages.price_label")}</label>
              <input type="number" step="0.01" min="0" value={newPriceEUR} onChange={e => setNewPriceEUR(e.target.value)} placeholder="2100.00" />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input type="checkbox" checked={newIsPublic} onChange={e => setNewIsPublic(e.target.checked)} />
              <span>{t("packages.public_label")}</span>
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="submit" className="btn-primary" disabled={creating || !newName.trim()}>
                {creating ? "Creating…" : "Create package"}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowNew(false)}>{t("packages.cancel")}</button>
            </div>
          </form>
        </div>
      )}

      {loading && <p style={{ color: "var(--muted)" }}>Loading…</p>}

      {!loading && packages.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--muted)" }}>
          {t("packages.empty")}
        </div>
      )}

      {!loading && packages.length > 0 && (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {packages.map((p, index) => (
            <div key={p.ID} className="card">
              {editID === p.ID ? (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  <h4 style={{ margin: 0 }}>{t("packages.edit_title")}</h4>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t("packages.name_label")}</label>
                    <input required value={editName} onChange={e => setEditName(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t("packages.description_label")}</label>
                    <textarea rows={2} value={editDescription} onChange={e => setEditDescription(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t("packages.price_label")}</label>
                    <input type="number" step="0.01" min="0" value={editPriceEUR} onChange={e => setEditPriceEUR(e.target.value)} />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input type="checkbox" checked={editIsPublic} onChange={e => setEditIsPublic(e.target.checked)} />
                    <span>{t("packages.public_label")}</span>
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn-primary" disabled={saving || !editName.trim()} onClick={() => void handleSave(p)}>
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button className="btn-secondary" onClick={cancelEdit}>{t("packages.cancel")}</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.25rem" }}>
                      <strong style={{ fontSize: "1rem" }}>{p.Name}</strong>
                      <span style={{
                        fontSize: "0.75rem",
                        padding: "0.1rem 0.5rem",
                        borderRadius: 999,
                        background: p.IsPublic ? "color-mix(in srgb, var(--accent) 15%, transparent)" : "var(--border)",
                        color: p.IsPublic ? "var(--accent)" : "var(--muted)",
                        fontWeight: 600,
                      }}>
                        {p.IsPublic ? "Public" : "Private"}
                      </span>
                    </div>
                    <div style={{ color: "var(--accent)", fontWeight: 600, marginBottom: p.Description ? "0.25rem" : 0 }}>
                      {formatPrice(p.PriceCents, p.Currency)}
                    </div>
                    {p.Description && (
                      <p style={{ margin: 0, color: "var(--muted)", fontSize: "0.875rem", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const, overflow: "hidden" }}>
                        {p.Description}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: "0.3rem" }}>
                      <button
                        className="btn-ghost"
                        style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem" }}
                        onClick={() => moveUp(index)}
                        disabled={index === 0 || moving}
                        title="Move up"
                      >↑</button>
                      <button
                        className="btn-ghost"
                        style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem" }}
                        onClick={() => moveDown(index)}
                        disabled={index === packages.length - 1 || moving}
                        title="Move down"
                      >↓</button>
                    </div>
                    <button
                      className="btn-ghost"
                      style={{ padding: "0.2rem 0.6rem", fontSize: "0.8rem" }}
                      onClick={() => startEdit(p)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-ghost"
                      style={{ padding: "0.2rem 0.6rem", fontSize: "0.8rem", color: "#ef4444" }}
                      onClick={() => void handleDelete(p.ID)}
                      disabled={deletingID === p.ID}
                    >
                      {deletingID === p.ID ? "…" : "Delete"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <div style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <button className="btn-ghost" onClick={() => setShowNew(v => !v)} style={{ fontSize: "0.875rem" }}>
            {showNew ? t("packages.cancel") : t("packages.new")}
          </button>
          {packages.length > 0 && (
            <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--muted)" }}>
              {t("packages.footnote")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
